import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import A_Input from '@/components/A_Atoms/A_Input/A_Input';
import A_TextArea from '@/components/A_Atoms/A_TextArea/A_TextArea';
import styles from './CapsuleCreatePage.module.css';
import O_CreationCapsuleActivation from '@/components/O_Organisms/O_CreationCapsuleActivation/O_CreationCapsuleActivation';
// import O_CreationCapsuleRepeat from '@/components/O_Organisms/O_CreationCapsuleRepeat/O_CreationCapsuleRepeat';
// import O_CreationCapsuleCoauthors from '@/components/O_Organisms/O_CreationCapsuleCoauthors/O_CreationCapsuleCoauthors';
import O_CreationCapsuleViewers from '@/components/O_Organisms/O_CreationCapsuleViewers/O_CreationCapsuleViewers';
import M_SelectPicker from '@/components/M_Molecules/M_SelectPicker/M_SelectPicker';
import cn from 'classnames';
import Q_BlurEffect from '@/components/Q_Quarks/Q_BlurEffect/Q_BlurEffect';
import { ContentItem } from '@/components/M_Molecules/M_ContentCard/M_ContentCard';
import O_CapsuleCreateHeaderResponsive from '@/components/O_Organisms/O_CapsuleCreateHeader/O_CapsuleCreateHeaderResponsive';
import CapsuleCreatePreview from './CapsuleCreatePreview';
import CapsuleCreateContentView from './CapsuleCreateContentView';
import {
  Capsule,
  ScenarioConfig,
  ScenarioReaction,
  capsulesApi,
} from '@/services/capsulesApi';
import {
  useCreateCapsule,
  useUpdateCapsule,
  useCapsule,
} from '@/hooks/useCapsules';
import S_AddCapsuleContentPopup from '@/components/S_Super-Organisms/S_AddCapsuleContentPopup/S_AddCapsuleContentPopup';
import O_HeaderBurgerMenu from '@/components/O_Organisms/O_Header/O_HeaderBurgerMenu';
import { useTranslation } from '@/hooks/useTranslation';
import { PATHS } from '@/constants/paths';
import { useAuth } from '@/contexts/AuthContext';
import A_Notification from '@/components/A_Atoms/A_Notification/A_Notification';
import { User } from '@/types/user';
import {
  CapsuleScenarioOption,
  getScenarioById,
  getScenariosWithPurchaseStatus,
} from '@/constants/capsuleScenarios';
import ReactionFallback from '@/assets/images/A_UserAvatar.png';

type TabKey = 'settings' | 'content' | 'preview';

const getDefaultScenario = () => {
  const scenariosWithStatus = getScenariosWithPurchaseStatus();
  return (
    scenariosWithStatus.find(item => item.unlocked) ??
    scenariosWithStatus[0] ??
    null
  );
};

const defaultScenario = getDefaultScenario();

export default function CapsuleCreatePage() {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const t = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUser = user;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: [] as ContentItem[],
    scheduledDate: '',
    coauthors: [] as string[],
    coauthorsEmails: [] as string[],
    viewers: [] as string[],
    viewersEmails: [] as string[],
    privacy: 'public' as 'public' | 'coauthors' | 'selected',
    activationType: 'manual' as 'manual' | 'scheduled' | 'custodian',
  });

  const [selectedTab, setSelectedTab] = useState<TabKey>('settings');
  const [isAddCapsuleContentPopupOpen, setIsAddCapsuleContentPopupOpen] =
    useState(false);
  const [isBurgerMenuOpen, setIsBurgerMenuOpen] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(
    defaultScenario?.id ?? null
  );
  const [scenarioFromCapsule, setScenarioFromCapsule] = useState<
    ScenarioConfig | undefined
  >(undefined);

  const tabLabels: Record<TabKey, string> = {
    settings: t('capsuleCreate.tabs.settings'),
    content: t('capsuleCreate.tabs.content'),
    preview: t('capsuleCreate.tabs.scenarios'),
  };

  const tabOrder: TabKey[] = ['settings', 'content', 'preview'];

  const buildScenarioConfig = useCallback(
    (option: CapsuleScenarioOption | null): ScenarioConfig | undefined => {
      if (!option) {
        return undefined;
      }

      const previewReaction: ScenarioReaction = {
        id: `${option.id}-preview-reaction`,
        text: t(option.previewComment.textKey),
        createdAt: option.previewComment.timestamp,
        author: {
          id: `${option.id}-preview-author`,
          name: t(option.previewComment.authorKey),
          avatar: ReactionFallback,
        },
        position: option.commentPosition,
        commentObject: option.commentObject,
        replies: [],
        isGuest: true,
      };

      return {
        id: option.id,
        backgroundUrl: option.background,
        commentObject: option.commentObject,
        comments: [previewReaction],
      };
    },
    [t]
  );

  const selectedScenarioOption = useMemo(() => {
    if (!selectedScenarioId) {
      return null;
    }
    return getScenarioById(selectedScenarioId);
  }, [selectedScenarioId]);

  const scenarioConfig = useMemo<ScenarioConfig | undefined>(() => {
    if (
      scenarioFromCapsule &&
      selectedScenarioId &&
      scenarioFromCapsule.id === selectedScenarioId
    ) {
      return scenarioFromCapsule;
    }
    return buildScenarioConfig(selectedScenarioOption);
  }, [
    buildScenarioConfig,
    selectedScenarioOption,
    scenarioFromCapsule,
    selectedScenarioId,
  ]);

  const { data: existingCapsule } = useCapsule(id || '');

  useEffect(() => {
    if (isEditMode && id && existingCapsule) {
      const capsule = existingCapsule;
      const scheduledAt = capsule.meta?.scheduledAt;
      const isOpened = Boolean(capsule.meta?.isOpened);
      const hasCoauthors = (capsule.privacy?.coauthors?.length ?? 0) > 0;
      const baseActivationType: 'manual' | 'scheduled' | 'custodian' =
        capsule.meta?.activationType ??
        (scheduledAt
          ? 'scheduled'
          : isOpened
            ? 'manual'
            : hasCoauthors
              ? 'custodian'
              : 'manual');
      setFormData({
        title: capsule.title,
        description: capsule.description || '',
        content: capsule.content as ContentItem[],
        scheduledDate: scheduledAt || '',
        coauthors:
          capsule.privacy?.coauthors?.map((c: { id: string }) => c.id) || [],
        coauthorsEmails: [],
        viewers: capsule.privacy?.allowedUserIds || [],
        viewersEmails: [],
        privacy:
          capsule.privacy?.level === 'public'
            ? 'public'
            : capsule.privacy?.level === 'coauthors'
              ? 'coauthors'
              : 'selected',
        activationType: baseActivationType,
      });

      if (baseActivationType === 'manual' && !scheduledAt && !isOpened) {
        void capsulesApi
          .getCapsuleInvites(id)
          .then(res => {
            if (!res.success) return;
            if (Array.isArray(res.data) && res.data.length > 0) {
              setFormData(prev =>
                prev.activationType === 'manual'
                  ? { ...prev, activationType: 'custodian' }
                  : prev
              );
            }
          })
          .catch(() => {
            // ignore
          });
      }
      if (capsule.scenario) {
        setScenarioFromCapsule(capsule.scenario);
        setSelectedScenarioId(capsule.scenario.id);
      }
    }
  }, [isEditMode, id, existingCapsule]);

  const handleTitleChange = (value: string) => {
    setFormData(prev => ({ ...prev, title: value }));
  };

  const handleDescriptionChange = (value: string) => {
    setFormData(prev => ({ ...prev, description: value }));
  };

  const handleTabSelect = (tab: string) => {
    const matchedTab = tabOrder.find(key => tabLabels[key] === tab);
    if (matchedTab) {
      setSelectedTab(matchedTab);
    }
  };

  const handleActivationTypeChange = (
    type: 'manual' | 'scheduled' | 'custodian'
  ) => {
    setFormData(prev => ({ ...prev, activationType: type }));
  };

  const handleSaveContent = (contentItem: ContentItem) => {
    setFormData(prev => ({
      ...prev,
      content: [...prev.content, contentItem],
    }));
    setSelectedTab('content');
  };

  const getAuthorName = () => {
    if (!currentUser) {
      return t('capsuleCreate.authorName');
    }
    const firstName = currentUser.personal.firstName?.trim() || '';
    const lastName = currentUser.personal.lastName?.trim() || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    return (
      fullName ||
      currentUser.handle ||
      currentUser.contact.email ||
      t('capsuleCreate.authorName')
    );
  };

  const mapPrivacyLevel = (
    privacy: 'public' | 'coauthors' | 'selected'
  ): Capsule['privacy']['level'] => privacy;

  const buildContentForCapsule = () => {
    return formData.content.map((item, index) => ({
      id: item.id || `content-${index}`,
      type: item.type as Capsule['content'][number]['type'],
      url: item.url,
      caption: item.caption,
      title: item.title,
      description: item.description,
      order: index + 1,
    }));
  };

  const draftCapsule: Capsule = {
    id: 'draft',
    title: formData.title || t('capsuleCreate.draftTitle'),
    description: formData.description || '',
    content: formData.content as any,
    scenario: scenarioConfig,
    meta: {
      createdAt: new Date().toISOString(),
      firstOpenedAt: undefined,
      scheduledAt: formData.scheduledDate || undefined,
      isOpened: false,
    },
    author: {
      id: currentUser?.id || '',
      name: getAuthorName(),
      avatar: currentUser?.avatar || '',
    },
    tree: undefined,
    engagement: {
      comments: 0,
    },
    privacy: {
      level: mapPrivacyLevel(formData.privacy),
      allowedUserIds: formData.viewers,
      coauthors: (formData.coauthors || []).map(id => ({ id }) as any),
    },
  };

  const createCapsuleMutation = useCreateCapsule();
  const updateCapsuleMutation = useUpdateCapsule();

  const handlePublish = async () => {
    try {
      const content = buildContentForCapsule();
      const privacyLevel = mapPrivacyLevel(formData.privacy);

      const allowedUserIds = formData.viewers
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));

      const coauthorsIds = formData.coauthors
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));
      const coauthorsEmails = Array.from(
        new Set(
          formData.coauthorsEmails
            .map(email => email.trim())
            .filter(Boolean)
            .map(email => email.toLowerCase())
        )
      );
      const viewersEmails = Array.from(
        new Set(
          formData.viewersEmails
            .map(email => email.trim())
            .filter(Boolean)
            .map(email => email.toLowerCase())
        )
      );

      const requestData: any = {
        title: formData.title.trim() || t('capsuleCreate.draftTitle'),
        description: formData.description.trim() || undefined,
        content: content.length > 0 ? content : undefined,
        privacy_level: privacyLevel,
      };

      if (formData.activationType === 'scheduled' && formData.scheduledDate) {
        requestData.scheduled_at = formData.scheduledDate;
        requestData.activation_policy = 'scheduled_time';
      } else if (formData.activationType === 'custodian') {
        requestData.activation_policy = 'after_death';
      } else {
        requestData.activation_policy = 'by_request';
      }

      if (privacyLevel === 'selected') {
        requestData.allowedUserIds =
          allowedUserIds.length > 0 ? allowedUserIds : [];
        if (viewersEmails.length > 0) {
          requestData.allowedUserEmails = viewersEmails;
        }
      } else if (allowedUserIds.length > 0) {
        requestData.allowedUserIds = allowedUserIds;
      } else if (viewersEmails.length > 0) {
        requestData.allowedUserEmails = viewersEmails;
      }

      if (coauthorsIds.length > 0) {
        requestData.coauthorsIds = coauthorsIds;
      }
      if (coauthorsEmails.length > 0) {
        requestData.coauthorsEmails = coauthorsEmails;
      }

      if (scenarioConfig) {
        const scenarioString = JSON.stringify(scenarioConfig);
        requestData.scenario = scenarioString;
      }

      Object.keys(requestData).forEach(key => {
        if (requestData[key] === undefined) {
          delete requestData[key];
        }
      });

      if (isEditMode && id) {
        await updateCapsuleMutation.mutate({
          id,
          data: requestData,
        });
        navigate(PATHS.CAPSULES, {
          state: { notification: 'createSuccess' },
        });
      } else {
        await createCapsuleMutation.mutate(requestData);
        navigate(PATHS.CAPSULES, {
          state: { notification: 'createSuccess' },
        });
      }
    } catch {
      setNotification({
        type: 'error',
        message: t('myCapsules.notifications.createError'),
      });
    }
  };

  const shouldShowHeader = selectedTab !== 'preview';

  return (
    <div className={styles.wrapper}>
      {shouldShowHeader && (
        <O_CapsuleCreateHeaderResponsive
          onSaveContent={handleSaveContent}
          onOpenAddContent={() => setIsAddCapsuleContentPopupOpen(true)}
          onOpenBurgerMenu={() => setIsBurgerMenuOpen(true)}
          showPreviewButton={false}
          showUploadButton={
            selectedTab === 'content' && formData.content.length > 0
          }
        />
      )}
      {isAddCapsuleContentPopupOpen && (
        <S_AddCapsuleContentPopup
          setIsAddCapsuleContentPopupOpen={setIsAddCapsuleContentPopupOpen}
          onSaveContent={handleSaveContent}
        />
      )}
      <O_HeaderBurgerMenu
        isOpen={isBurgerMenuOpen}
        onClose={() => setIsBurgerMenuOpen(false)}
      />
      {selectedTab === 'settings' && (
        <div className={styles.content}>
          <div className={styles.left_wrapper}>
            <A_Input
              label={t('capsuleCreate.titleLabel')}
              value={formData.title}
              onChange={handleTitleChange}
              type='text'
              maxLength={150}
              borderRadius='text-fields'
            />
            <A_TextArea
              label={t('capsuleCreate.descriptionLabel')}
              value={formData.description}
              onChange={handleDescriptionChange}
              maxLength={2000}
              height={678}
            />
          </div>
          <div className={styles.right_wrapper}>
            <O_CreationCapsuleActivation
              activationType={formData.activationType}
              onActivationTypeChange={handleActivationTypeChange}
              scheduledDate={formData.scheduledDate}
              onDateChange={date =>
                setFormData(prev => ({ ...prev, scheduledDate: date }))
              }
              selectedCustodians={formData.coauthors}
              onCustodianSelect={(user: User) => {
                const userId = String(user.id);
                if (!formData.coauthors.includes(userId)) {
                  setFormData(prev => ({
                    ...prev,
                    coauthors: [...prev.coauthors, userId],
                  }));
                }
              }}
              onCustodianEmailInvite={(email: string) =>
                setFormData(prev => {
                  const normalized = email.trim().toLowerCase();
                  if (!normalized) {
                    return prev;
                  }
                  if (prev.coauthorsEmails.includes(normalized)) {
                    return prev;
                  }
                  return {
                    ...prev,
                    coauthorsEmails: [...prev.coauthorsEmails, normalized],
                  };
                })
              }
              onCustodianRemove={userId =>
                setFormData(prev => ({
                  ...prev,
                  coauthors: prev.coauthors.filter(id => id !== userId),
                }))
              }
              capsuleId={isEditMode ? id : undefined}
            />
            {/* <O_CreationCapsuleRepeat /> */}
            {/* TODO: раскомментить во второй версии */}
            {/* <O_CreationCapsuleCoauthors
              capsuleId={isEditMode ? id : undefined}
              selectedCoauthors={formData.coauthors}
              onUserSelect={(user: User) => {
                const userId = String(user.id);
                if (!formData.coauthors.includes(userId)) {
                  setFormData(prev => ({
                    ...prev,
                    coauthors: [...prev.coauthors, userId],
                  }));
                }
              }}
            /> */}
            <O_CreationCapsuleViewers
              privacy={formData.privacy}
              onPrivacyChange={p =>
                setFormData(prev => ({ ...prev, privacy: p }))
              }
              viewers={formData.viewers}
              onViewersChange={viewers =>
                setFormData(prev => ({ ...prev, viewers }))
              }
              onViewerEmailInvite={email =>
                setFormData(prev => {
                  const normalized = email.trim().toLowerCase();
                  if (!normalized || prev.viewersEmails.includes(normalized)) {
                    return prev;
                  }
                  return {
                    ...prev,
                    viewersEmails: [...prev.viewersEmails, normalized],
                  };
                })
              }
              capsuleId={isEditMode ? id : undefined}
            />
          </div>
        </div>
      )}
      {selectedTab === 'content' && (
        <CapsuleCreateContentView
          content={formData.content}
          capsule={draftCapsule}
          onSaveContent={handleSaveContent}
        />
      )}
      {selectedTab === 'preview' && (
        <CapsuleCreatePreview
          content={formData.content}
          capsule={draftCapsule}
          onExit={() => setSelectedTab('settings')}
          onSaveContent={handleSaveContent}
          onPublish={handlePublish}
        />
      )}
      {selectedTab !== 'preview' && (
        <div className={styles.bottom_wrapper}>
          <div
            className={cn(styles.select_picker_wrapper, 'effect-green-glow')}
          >
            <M_SelectPicker
              options={tabOrder.map(tab => tabLabels[tab])}
              selectedOption={tabLabels[selectedTab]}
              onSelect={handleTabSelect}
              background='white'
            />
          </div>
          <Q_BlurEffect withBackground />
        </div>
      )}
      {notification && (
        <A_Notification
          type={notification.type}
          message={notification.message}
          isVisible
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}
